import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation, TFunction } from 'react-i18next';
import { Button } from '@kizen/kds/Button';
import { deferExecution } from 'utility/defer';
import { KZN_FRAME_EVENTS } from '__pages/BusinessBuilderPublic/events';
import ConfirmationModal from '__components/Modals/ConfirmationModal';

type ControlsProps = {
  setNewMessage: (message: string) => void;
  onSendMessage: (
    message: string,
    isCustomMessage?: boolean,
    skipPills?: boolean,
    clearNewMessage?: boolean
  ) => void;
  onCreateNewChat: () => void;
  setDisplayAlertToast: () => void;
  newMessage: string;
  onCompleteSession: () => void;
  showBuildApplicationButton: boolean;
  showSkipQuestionsButton: boolean;
  showAskMoreQuestionsButton: boolean;
  showCreateNewChatButton: boolean;
  disableBuildApplicationButton: boolean;
  disableSkipQuestionsButton: boolean;
  disableCreateNewChatButton: boolean;
  disableAskMoreQuestionsButton: boolean;
  entityName?: string;
  trackEvent?: (event: string, payload?: any) => void;
  minHeight?: number;
};

export const BUSINESS_BUILDER_BUTTONS = {
  REVIEW_PLAN: {
    buttonText: (t: TFunction) => t('Review AI Plan'),
    message: (entity: string | undefined) =>
      `With the information already provided, generate my ${entity?.toLocaleLowerCase() ?? ''} requirements`,
  },
  START_OVER: {
    buttonText: (t: TFunction) => t('Start Over'),
    message: 'Start Over',
  },
  REFINE_PLAN: {
    buttonText: (t: TFunction) => t('Refine Plan'),
    message: (entity: string | undefined) =>
      `Ask more questions to refine my ${entity?.toLocaleLowerCase() ?? ''} generation plan`,
  },
};

export const Controls: FC<ControlsProps> = ({
  setNewMessage,
  onSendMessage,
  onCreateNewChat,
  setDisplayAlertToast,
  onCompleteSession,
  showBuildApplicationButton,
  showSkipQuestionsButton,
  showAskMoreQuestionsButton,
  showCreateNewChatButton,
  disableBuildApplicationButton,
  disableSkipQuestionsButton,
  disableCreateNewChatButton,
  disableAskMoreQuestionsButton,
  entityName,
  trackEvent,
  minHeight,
}) => {
  const { t } = useTranslation();
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  const visibleCount =
    Number(showCreateNewChatButton) +
    Number(showAskMoreQuestionsButton) +
    Number(showSkipQuestionsButton) +
    Number(showBuildApplicationButton);

  const prevCountRef = useRef(visibleCount);
  const [containerAnimKey, setContainerAnimKey] = useState(0);

  // remount group -> restart CSS animation
  useEffect(() => {
    if (visibleCount > prevCountRef.current) {
      setContainerAnimKey((k) => k + 1);
    }
    prevCountRef.current = visibleCount;
  }, [visibleCount]);

  const handleSendDefinedMessage = useCallback(
    (message: string, isCustomMessage?: boolean) => {
      onSendMessage(message, isCustomMessage, true, false);
      setNewMessage('');
    },
    [onSendMessage, setNewMessage]
  );

  const handleCreateNewChat = () => {
    trackEvent?.(KZN_FRAME_EVENTS.START_OVER);
    onCreateNewChat();
    setNewMessage('');
    deferExecution(() => setDisplayAlertToast());
  };

  const handleCompleteSession = () => {
    trackEvent?.(KZN_FRAME_EVENTS.BUILD);
    return onCompleteSession();
  };

  const handleCreatePlan = () => {
    trackEvent?.(KZN_FRAME_EVENTS.GENERATE_REQUIREMENTS);
    return handleSendDefinedMessage(
      BUSINESS_BUILDER_BUTTONS.REVIEW_PLAN.message(entityName),
      true
    );
  };

  const handleAskMoreQuestions = () => {
    trackEvent?.(KZN_FRAME_EVENTS.ASK_MORE_QUESTIONS);
    return handleSendDefinedMessage(
      BUSINESS_BUILDER_BUTTONS.REFINE_PLAN.message(entityName),
      true
    );
  };

  return (
    <div
      className="min-w-[250px] px-[15px] mt-[10px]"
      style={{ minHeight: `${minHeight}px` }}
    >
      <div
        key={containerAnimKey}
        className="flex flex-wrap justify-end items-center gap-y-[5px] gap-x-[15px] animate-fadeIn"
      >
        {showCreateNewChatButton ? (
          <div>
            <Button
              type="button"
              variant="text"
              color="warning"
              onClick={() => setShowCancelConfirmation(true)}
              disabled={disableCreateNewChatButton}
            >
              {BUSINESS_BUILDER_BUTTONS.START_OVER.buttonText(t)}
            </Button>
          </div>
        ) : null}

        {showAskMoreQuestionsButton ? (
          <div>
            <Button
              type="button"
              color="secondary"
              outline
              onClick={handleAskMoreQuestions}
              disabled={disableAskMoreQuestionsButton}
            >
              {BUSINESS_BUILDER_BUTTONS.REFINE_PLAN.buttonText(t)}
            </Button>
          </div>
        ) : null}

        {showSkipQuestionsButton ? (
          <div>
            <Button
              color="primary"
              type="button"
              maxWidth={false}
              onClick={handleCreatePlan}
              disabled={disableSkipQuestionsButton}
            >
              {BUSINESS_BUILDER_BUTTONS.REVIEW_PLAN.buttonText(t)}
            </Button>
          </div>
        ) : null}

        {showBuildApplicationButton ? (
          <div>
            <Button
              type="button"
              onClick={handleCompleteSession}
              disabled={disableBuildApplicationButton}
            >
              {entityName
                ? t('Build My {{entityName}}', { entityName })
                : t('Build')}
            </Button>
          </div>
        ) : null}
      </div>

      <ConfirmationModal
        heading={t('Start Your AI Building Session Over')}
        buttonText={t('Confirm')}
        defaultLeftBtnText={t('Cancel')}
        actionBtnColor="green"
        show={showCancelConfirmation}
        onConfirm={async () => {
          handleCreateNewChat();
          setShowCancelConfirmation(false);
        }}
        onHide={() => setShowCancelConfirmation(false)}
      >
        {t('Your current session will be lost, would you like to continue?')}
      </ConfirmationModal>
    </div>
  );
};
